/**
 * Jeu d'icônes de Finance Prévision.
 *
 * Toutes les icônes partagent la même grille 24×24, le même trait de 1,5 px et
 * les mêmes terminaisons arrondies : c'est cette régularité, plus que le dessin
 * de chaque symbole, qui donne un rendu professionnel. Elles héritent de la
 * couleur du texte parent (`stroke="currentColor"`), donc elles suivent le
 * thème sans réglage.
 *
 * Elles remplacent les emojis, dont le rendu changeait d'un système à l'autre.
 *
 *   <Icon name="wallet" />                 → 20px, décorative
 *   <Icon name="trash" size={16} />
 *   <Icon name="search" title="Chercher" /> → exposée aux lecteurs d'écran
 */

const paths = {
  // — Navigation ————————————————————————————————————————————————
  dashboard: (
    <>
      <rect x="3" y="3" width="7.5" height="9" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="5.5" rx="1.5" />
      <rect x="13.5" y="12" width="7.5" height="9" rx="1.5" />
      <rect x="3" y="15.5" width="7.5" height="5.5" rx="1.5" />
    </>
  ),
  operations: (
    <>
      <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3h13A1.5 1.5 0 0 1 20 4.5V21l-2.7-1.6-2.65 1.6-2.65-1.6L9.35 21 6.7 19.4 4 21Z" />
      <path d="M8.5 8.5h7M8.5 12.5h7M8.5 16h4" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v15.5A2.5 2.5 0 0 0 5.5 21H21" />
      <path d="M7 15.5l3.75-4.25 3 2.5L20 6.5" />
      <path d="M20 10.5v-4h-4" />
    </>
  ),
  settings: (
    <>
      <path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z" />
      <path d="M19.05 15.05a1.6 1.6 0 0 0 .32 1.77l.06.06a1.94 1.94 0 1 1-2.75 2.75l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-.97 1.47v.17a1.94 1.94 0 1 1-3.88 0v-.09a1.6 1.6 0 0 0-1.05-1.46 1.6 1.6 0 0 0-1.77.32l-.06.06a1.94 1.94 0 1 1-2.75-2.75l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-.97h-.17a1.94 1.94 0 1 1 0-3.88h.09a1.6 1.6 0 0 0 1.46-1.05 1.6 1.6 0 0 0-.32-1.77l-.06-.06a1.94 1.94 0 1 1 2.75-2.75l.06.06a1.6 1.6 0 0 0 1.77.32h.08a1.6 1.6 0 0 0 .97-1.47v-.17a1.94 1.94 0 1 1 3.88 0v.09a1.6 1.6 0 0 0 .97 1.46 1.6 1.6 0 0 0 1.77-.32l.06-.06a1.94 1.94 0 1 1 2.75 2.75l-.06.06a1.6 1.6 0 0 0-.32 1.77v.08a1.6 1.6 0 0 0 1.47.97h.17a1.94 1.94 0 1 1 0 3.88h-.09a1.6 1.6 0 0 0-1.46.97Z" />
    </>
  ),
  home: (
    <>
      <path d="M3 10.2 12 3l9 7.2V19.5A1.5 1.5 0 0 1 19.5 21h-15A1.5 1.5 0 0 1 3 19.5Z" />
      <path d="M9.5 21v-8h5v8" />
    </>
  ),
  menu: <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" />,
  more: (
    <>
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="5" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),

  // — Actions ————————————————————————————————————————————————————
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  close: <path d="M18 6 6 18M6 6l12 12" />,
  check: <path d="m4.5 12.5 5 5 10-11" />,
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m20 20-4.9-4.9" />
    </>
  ),
  filter: <path d="M3.5 5.5h17l-6.8 8v6l-3.4 1.8v-7.8Z" />,
  edit: (
    <>
      <path d="M12.5 5.5H5.5A1.5 1.5 0 0 0 4 7v11.5A1.5 1.5 0 0 0 5.5 20H17a1.5 1.5 0 0 0 1.5-1.5v-7" />
      <path d="M17.4 3.6a1.98 1.98 0 0 1 2.8 2.8L12.5 14l-3.5 1 1-3.5Z" />
    </>
  ),
  trash: (
    <>
      <path d="M3.5 6.5h17M9 6.5V5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5v1.5" />
      <path d="M18 6.5v13A1.5 1.5 0 0 1 16.5 21h-9A1.5 1.5 0 0 1 6 19.5v-13" />
      <path d="M10 11v5.5M14 11v5.5" />
    </>
  ),
  refresh: (
    <>
      <path d="M20.5 11a8.5 8.5 0 0 0-14.7-4.6L3.5 8.5" />
      <path d="M3.5 13a8.5 8.5 0 0 0 14.7 4.6l2.3-2.1" />
      <path d="M3.5 4v4.5H8M20.5 20v-4.5H16" />
    </>
  ),
  download: (
    <>
      <path d="M4 15.5v3A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5v-3" />
      <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5" />
    </>
  ),
  logout: (
    <>
      <path d="M9.5 20H5.5A1.5 1.5 0 0 1 4 18.5v-13A1.5 1.5 0 0 1 5.5 4h4" />
      <path d="M15.5 16.5 20 12l-4.5-4.5M20 12H9.5" />
    </>
  ),

  // — Chevrons et flèches ————————————————————————————————————————
  chevronDown: <path d="m6 9.5 6 6 6-6" />,
  chevronUp: <path d="m6 14.5 6-6 6 6" />,
  chevronLeft: <path d="m14.5 6-6 6 6 6" />,
  chevronRight: <path d="m9.5 6 6 6-6 6" />,
  arrowUp: <path d="M12 19.5v-15M5.5 11 12 4.5 18.5 11" />,
  arrowDown: <path d="M12 4.5v15M5.5 13 12 19.5 18.5 13" />,
  trendUp: (
    <>
      <path d="m3.5 16.5 5.5-5.5 3.5 3.5L20.5 7" />
      <path d="M20.5 11.5v-4.5H16" />
    </>
  ),
  trendDown: (
    <>
      <path d="m3.5 7.5 5.5 5.5 3.5-3.5 8 8" />
      <path d="M20.5 12.5v4.5H16" />
    </>
  ),

  // — États ——————————————————————————————————————————————————————
  info: (
    <>
      <circle cx="12" cy="12" r="8.75" />
      <path d="M12 16.5v-5M12 8.1h.01" />
    </>
  ),
  alert: (
    <>
      <path d="M10.7 4.1 2.6 17.6a1.5 1.5 0 0 0 1.3 2.3h16.2a1.5 1.5 0 0 0 1.3-2.3L13.3 4.1a1.5 1.5 0 0 0-2.6 0Z" />
      <path d="M12 9.5v4M12 17h.01" />
    </>
  ),
  checkCircle: (
    <>
      <circle cx="12" cy="12" r="8.75" />
      <path d="m8.2 12.3 2.6 2.6 5-5.4" />
    </>
  ),
  alertCircle: (
    <>
      <circle cx="12" cy="12" r="8.75" />
      <path d="M12 7.5v5M12 16h.01" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="8.75" />
      <path d="M9.6 9.6a2.5 2.5 0 1 1 3.4 2.3c-.7.3-1 .9-1 1.6v.4" />
      <path d="M12 17h.01" />
    </>
  ),

  // — Finance ————————————————————————————————————————————————————
  wallet: (
    <>
      <path d="M4 7.5A1.5 1.5 0 0 1 5.5 6h13A1.5 1.5 0 0 1 20 7.5v11A1.5 1.5 0 0 1 18.5 20h-13A1.5 1.5 0 0 1 4 18.5Z" />
      <path d="M4 9.5V6.2A1.7 1.7 0 0 1 5.5 4.5h10" />
      <path d="M20 11.5h-3.5a1.5 1.5 0 0 0 0 3H20" />
    </>
  ),
  bank: (
    <>
      <path d="M3.5 9.5 12 4l8.5 5.5" />
      <path d="M5.5 9.5v8M10 9.5v8M14 9.5v8M18.5 9.5v8" />
      <path d="M3 20.5h18" />
    </>
  ),
  card: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3 10h18" />
      <path d="M7 14.5h3" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M3.5 10h17M8.5 3v4M15.5 3v4" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.75" />
      <circle cx="12" cy="12" r="4.75" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  gauge: (
    <>
      <path d="M4 16a8.5 8.5 0 1 1 16 0" />
      <path d="m12 16 4-5" />
      <circle cx="12" cy="16" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),

  // — Thème et compte ————————————————————————————————————————————
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
    </>
  ),
  moon: <path d="M20.5 14.3A8.7 8.7 0 0 1 9.7 3.5a8.75 8.75 0 1 0 10.8 10.8Z" />,
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  lock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M9.9 5.8A9.8 9.8 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.7 3.6M6.2 7.5A17 17 0 0 0 2.5 12S6 18.5 12 18.5c1.4 0 2.6-.3 3.7-.8" />
      <path d="M10 10a2.8 2.8 0 0 0 4 4" />
      <path d="m3 3 18 18" />
    </>
  ),
  // — Catégories de dépenses ————————————————————————————————————
  cart: (
    <>
      <circle cx="9.5" cy="20" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="18" cy="20" r="1.2" fill="currentColor" stroke="none" />
      <path d="M2.5 3.5h2.6l2.3 11.2a1.6 1.6 0 0 0 1.6 1.3h8.4a1.6 1.6 0 0 0 1.6-1.25l1.5-6.75H6" />
    </>
  ),
  food: (
    <>
      <path d="M5 3v7a2.5 2.5 0 0 0 5 0V3" />
      <path d="M7.5 10.5V21" />
      <path d="M17.5 3c-1.7 1.2-2.5 3-2.5 5.5s.8 3.5 2.5 3.5h1V3Z" />
      <path d="M18.5 12v9" />
    </>
  ),
  car: (
    <>
      <path d="M4.5 16.5v2a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-2M21.5 16.5v2a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-2" />
      <path d="M2.5 16.5v-4l2-5A1.5 1.5 0 0 1 6 6.5h12a1.5 1.5 0 0 1 1.5 1l2 5v4Z" />
      <path d="M2.5 12.5h19M6.5 9.5h11" />
      <circle cx="6.5" cy="14.5" r=".9" fill="currentColor" stroke="none" />
      <circle cx="17.5" cy="14.5" r=".9" fill="currentColor" stroke="none" />
    </>
  ),
  heart: <path d="M12 20.3 3.9 12.2a5 5 0 1 1 7.1-7.05l1 1 1-1a5 5 0 1 1 7.1 7.05Z" />,
  gift: (
    <>
      <rect x="3" y="8.5" width="18" height="4" rx="1" />
      <path d="M4.5 12.5v7A1.5 1.5 0 0 0 6 21h12a1.5 1.5 0 0 0 1.5-1.5v-7M12 8.5V21" />
      <path d="M12 8.5H7.75a2.375 2.375 0 1 1 0-4.75C11 3.75 12 8.5 12 8.5ZM12 8.5h4.25a2.375 2.375 0 1 0 0-4.75C13 3.75 12 8.5 12 8.5Z" />
    </>
  ),
  briefcase: (
    <>
      <rect x="2.5" y="7" width="19" height="13" rx="2" />
      <path d="M8.5 7V5.5A1.5 1.5 0 0 1 10 4h4a1.5 1.5 0 0 1 1.5 1.5V7" />
      <path d="M2.5 12.5h19" />
    </>
  ),
  plug: (
    <>
      <path d="M9 3v6M15 3v6" />
      <path d="M5.5 9h13v3a6.5 6.5 0 0 1-13 0Z" />
      <path d="M12 18.5V21" />
    </>
  ),
  book: (
    <>
      <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19v18H5.5A1.5 1.5 0 0 1 4 19.5Z" />
      <path d="M4 17.5A1.5 1.5 0 0 1 5.5 16H19" />
    </>
  ),
  plane: <path d="M10.5 20.5 12 15l7.7 2.2a1.2 1.2 0 0 0 1.5-1.5L18.6 7l2.2-2.2a2 2 0 0 0-2.8-2.8L15.8 4.2 7.1 1.6a1.2 1.2 0 0 0-1.5 1.5L8 10.8 3.4 15.4l-1.9-.5 1.5 3.6 3.6 1.5-.5-1.9Z" />,
  phone: (
    <>
      <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
      <path d="M11 18.5h2" />
    </>
  ),
  piggy: (
    <>
      <path d="M18.5 12.5a6.5 6.5 0 0 0-6.5-6h-1a6 6 0 0 0-5.9 5H3.5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h1.3a6 6 0 0 0 2.2 2.9V20a1 1 0 0 0 1 1h1.5a1 1 0 0 0 1-1v-.6h2v.6a1 1 0 0 0 1 1H15a1 1 0 0 0 1-1v-1.6a6 6 0 0 0 2.5-4.9Z" />
      <path d="M15.5 11h.01M11 6.5l1.5-3" />
    </>
  ),
  swap: (
    <>
      <path d="M17 3.5 20.5 7 17 10.5M20.5 7H8.5" />
      <path d="M7 13.5 3.5 17 7 20.5M3.5 17h12" />
    </>
  ),
}

export default function Icon({ name, size = 20, className = '', title, strokeWidth = 1.5, ...rest }) {
  const d = paths[name]
  if (!d) {
    if (import.meta.env?.DEV) console.warn(`Icon: « ${name} » n'existe pas dans le jeu d'icônes.`)
    return null
  }

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...rest}
    >
      {title && <title>{title}</title>}
      {d}
    </svg>
  )
}

export const iconNames = Object.keys(paths)
